import { useMemo } from 'react'

import { useCubeReportPageFiltersContext } from 'cube'
import { MEETINGS_DIMENSIONS, MEETINGS_MEASURES } from 'cube/constants'
import { MeetingCountQuery } from 'cube/queries'
import moment from 'moment'

import {
  CubeQueryBuilder,
  resultSetAsChartData,
  useCubeQuery,
} from '@driftt/dds.reports'

import {
  SOURCE_HUMANIZED_TRANSLATION,
  STATUS_HUMANIZED_TRANSLATION,
} from 'constants/meetings'
import useTimePeriod from 'hooks/useTimePeriod'

export interface IMeetingCount {
  metric?: any[] | null
  previousMetric?: number | null
  isLoading: boolean
  hasNoData: boolean
  hasError: boolean
}

const formatSeriesData = (resultSet) => {
  const data = resultSetAsChartData(resultSet)

  if (!data || data.length < 1) {
    return null
  }
  const seriesObject = data?.reduce((acc, current) => {
    const count = current[MEETINGS_MEASURES.MEETING_COUNT]
    const status = current.xValues[0]
    const source = current.xValues[1]

    const sourceDisplay = SOURCE_HUMANIZED_TRANSLATION[source]
    const statusDisplay = STATUS_HUMANIZED_TRANSLATION[status]

    if (sourceDisplay in acc) {
      let isUpdated = false
      //some sources have different BE names but we want them to be treated as one source in the chart
      //this block updates the count instead of pushing a duplicate value
      acc[sourceDisplay].forEach((statusAndCount, index) => {
        if (statusAndCount[0] === statusDisplay) {
          acc[sourceDisplay][index][1] += count
          isUpdated = true
        }
      })
      //if we didn't update the count above, then the status hasn't been added to the set yet, add it now
      if (!isUpdated) acc[sourceDisplay].push([statusDisplay, count])
    } else {
      acc[sourceDisplay] = []
      acc[sourceDisplay].push([statusDisplay, count])
    }
    return acc
  }, {})
  return Object.entries(seriesObject).map(([key, value]) => {
    return {
      label: key,
      data: value,
    }
  })
}

export const useMeetingOutcomes = (): IMeetingCount => {
  const timePeriod = useTimePeriod()

  const { pageFilters = [] } = useCubeReportPageFiltersContext()

  const cubeQuery = new CubeQueryBuilder({
    query: MeetingCountQuery,
  })
    .withName('MeetingOutcomes')
    .withFilters([
      {
        member: MEETINGS_DIMENSIONS.MEETING_SOURCE,
        operator: 'equals',
        values: ['SITE_CONCIERGE'],
      },
      ...pageFilters,
    ])
    .withDimensions([
      MEETINGS_DIMENSIONS.STATUS,
      MEETINGS_DIMENSIONS.MEETING_SOURCE,
    ])
    .withTimeDimensions([
      {
        dimension: MEETINGS_DIMENSIONS.SCHEDULED_AT,
        dateRange: [
          moment(timePeriod.startDate).format('YYYY-MM-DD'),
          moment(timePeriod.endDate).format('YYYY-MM-DD'),
        ],
      },
    ])
    .build()

  const {
    data: resultSet,
    isLoading: isLoading,
    isError: isError,
  } = useCubeQuery({
    query: cubeQuery,
  })

  const meetingOutcomes = useMemo(
    () => formatSeriesData(resultSet),
    [resultSet]
  )

  const metric = useMemo(() => meetingOutcomes ?? null, [meetingOutcomes])

  const hasNoData = useMemo(
    () => !isLoading && !meetingOutcomes,
    [isLoading, meetingOutcomes]
  )

  const hasError = useMemo(() => !isLoading && isError, [isLoading, isError])

  return useMemo(
    () => ({ metric, isLoading, hasNoData, hasError }),
    [metric, isLoading, hasNoData, hasError]
  )
}

export default useMeetingOutcomes
